import moment, { Moment } from 'moment';
import { SelectOptionElement } from '@odin-labs/components';
import { AuthUser } from 'auth/types';
import { to } from 'acl';
import { BriefcaseIcon } from 'components/icons';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import {
  JobsiteCustomWorkerDataField,
  JobsiteWorker,
  Worker,
} from 'containers/worker/types';
import {
  toFancySelectOptions,
  tradeExperienceOptions as tradeExperienceOptionsValues,
} from 'utils/constants';
import {
  generateCustomWorkerDataFieldInputs,
  getDefaultValueForCustomDataType,
} from 'utils/onboarding';
import { DeepMap } from 'react-hook-form';
import { UpdateJobsiteWorkerMutationVariables } from 'apollo/generated/client-operations';
import { JobsiteOptionElement, FALSE, TRUE } from './types';

const tradeExperienceOptions = toFancySelectOptions(
  tradeExperienceOptionsValues,
);

export type EditJobsiteWorkerFormData = {
  helmetNumber: string;
  firstName: string;
  lastName: string;
  jobsiteId: JobsiteOptionElement;
  hasEscortPrivileges: SelectOptionElement;
  isExemptFromSwipeInactivity: SelectOptionElement;
  payrollId: string;
  isExemptFromSiteSpecificRefresher: SelectOptionElement;
  startDate: Moment;
  jobTitle: SelectOptionElement;
  trade: SelectOptionElement;
  tradeExperience: SelectOptionElement;
  unionAffiliation: SelectOptionElement;
  classificationGroup: SelectOptionElement;
};

const booleanOptions: SelectOptionElement[] = [
  {
    label: 'Yes',
    value: TRUE,
  },
  {
    label: 'No',
    value: FALSE,
  },
];

export const getFormInputs = ({
  jobsitesOptions,
  user,
  canAddUnions,
  tradeOptions,
  jobTitleOptions,
  unionAffiliationOptions,
  classificationGroupOptions,
  jobsiteCustomWorkerDataFields = [],
}: {
  jobsitesOptions: SelectOptionElement[];
  user: AuthUser;
  canAddUnions: boolean;
  tradeOptions: SelectOptionElement[];
  jobTitleOptions: SelectOptionElement[];
  unionAffiliationOptions?: SelectOptionElement[];
  classificationGroupOptions?: SelectOptionElement[];
  jobsiteCustomWorkerDataFields?: JobsiteCustomWorkerDataField[];
}): TypedFormInputs<EditJobsiteWorkerFormData> => ({
  firstName: {
    element: FormInputTypes.Field,
    label: 'First name',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { disabled: true },
  },
  lastName: {
    element: FormInputTypes.Field,
    label: 'Last name',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { disabled: true },
  },
  jobsiteId: {
    element: FormInputTypes.Select,
    label: 'Jobsite',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: {
      options: jobsitesOptions,
      icon: BriefcaseIcon,
      disabled: true,
    },
    validation: {
      required: true,
    },
  },
  helmetNumber: {
    element: FormInputTypes.Field,
    label: 'Helmet number',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { fieldType: 'number' },
  },
  trade: {
    element: FormInputTypes.Select,
    label: 'Trade',
    elementProps: {
      options: tradeOptions,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  tradeExperience: {
    element: FormInputTypes.Select,
    label: 'Trade experience',
    elementProps: {
      options: tradeExperienceOptions,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  jobTitle: {
    element: FormInputTypes.Select,
    label: 'Job title',
    elementProps: {
      options: jobTitleOptions,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  unionAffiliation: {
    element: FormInputTypes.Select,
    label: 'Union affiliation',
    elementProps: {
      isCreateable: canAddUnions,
      options: unionAffiliationOptions,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  classificationGroup: {
    element: FormInputTypes.Select,
    label: 'Classification group',
    elementProps: {
      options: classificationGroupOptions,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  hasEscortPrivileges: {
    element: FormInputTypes.Select,
    label: 'Allow escort privileges',
    layout: user.isAllowed(to.seeAllowEscortPrivileges)
      ? [GridColSpan.SpanFull, GridColSpan.SmSpan3]
      : 'odin-hidden',
    elementProps: { options: booleanOptions, isClearable: false },
    validation: { required: true },
  },
  isExemptFromSwipeInactivity: {
    element: FormInputTypes.Select,
    label: 'Exempt from 30 days of inactivity',
    layout: user.isAllowed(to.seeExemptFrom30DaysOfInactivity)
      ? [GridColSpan.SpanFull, GridColSpan.SmSpan3]
      : 'odin-hidden',
    elementProps: { options: booleanOptions, isClearable: false },
    validation: { required: true },
  },
  payrollId: {
    element: FormInputTypes.Field,
    label: 'Payroll ID',
    layout: user.isAllowed(to.seePayrollID)
      ? [GridColSpan.SpanFull, GridColSpan.SmSpan3]
      : 'odin-hidden',
  },
  isExemptFromSiteSpecificRefresher: {
    element: FormInputTypes.Select,
    label: 'Exempt from site refresher training',
    layout: user.isAllowed(to.seeExemptFromSiteRefresherTraining)
      ? [GridColSpan.SpanFull, GridColSpan.SmSpan3]
      : 'odin-hidden',
    elementProps: { options: booleanOptions, isClearable: false },
    validation: { required: true },
  },
  startDate: {
    element: FormInputTypes.DatePicker,
    label: 'Onboard date',
    elementProps: {
      showDefaultIcon: true,
      numberOfMonths: 1,
    },
    validation: {
      required: true,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  ...generateCustomWorkerDataFieldInputs(
    jobsiteCustomWorkerDataFields,
    GridColSpan.SmSpan3,
  ),
});

export const getDefaultValues = (args: {
  jobsitesOptions: JobsiteOptionElement[];
  jobsiteWorker: JobsiteWorker;
  worker: Worker;
  tradeOptions: SelectOptionElement[];
  jobTitleOptions: SelectOptionElement[];
  unionAffiliationOptions?: SelectOptionElement[];
  classificationGroupOptions?: SelectOptionElement[];
  jobsiteCustomWorkerDataFields?: JobsiteCustomWorkerDataField[];
}): EditJobsiteWorkerFormData => {
  const {
    worker,
    jobsiteWorker,
    jobsitesOptions,
    tradeOptions,
    jobTitleOptions,
    unionAffiliationOptions,
    classificationGroupOptions,
    jobsiteCustomWorkerDataFields = [],
  } = args;
  const {
    stickerNumber,
    isAntipassbackExempt,
    payrollId,
    isExemptFromSwipeInactivity,
    isExemptFromSiteSpecificRefresher,
    startDate,
    trade,
    tradeExperience,
    jobTitle,
    unionAffiliation,
    classificationGroup,
  } = jobsiteWorker ?? {};
  const { firstName, lastName } = worker?.user?.identity ?? {};

  return {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    jobsiteId: jobsitesOptions?.[0] ?? null,
    jobTitle: jobTitleOptions.find((opt) => opt.value === jobTitle) ?? null,
    trade: tradeOptions.find((opt) => opt.value === trade) ?? null,
    tradeExperience:
      tradeExperienceOptions.find((opt) => opt.value === tradeExperience) ??
      null,
    unionAffiliation:
      unionAffiliationOptions.find((opt) => opt.value === unionAffiliation) ??
      null,
    classificationGroup:
      classificationGroupOptions?.find(
        (opt) => opt.value === classificationGroup,
      ) ?? null,
    helmetNumber: stickerNumber ?? '',
    hasEscortPrivileges: booleanOptions.find(
      (opt) => opt.value === (isAntipassbackExempt ?? false).toString(),
    ),
    isExemptFromSwipeInactivity: booleanOptions.find(
      (opt) => opt.value === (isExemptFromSwipeInactivity ?? false).toString(),
    ),
    payrollId: payrollId ?? '',
    isExemptFromSiteSpecificRefresher: booleanOptions.find(
      (opt) =>
        opt.value === (isExemptFromSiteSpecificRefresher ?? false).toString(),
    ),
    startDate: startDate ? moment(startDate) : null,
    ...jobsiteCustomWorkerDataFields.reduce(
      (acc, customField) => ({
        ...acc,
        [customField.fieldLabel]:
          jobsiteWorker?.customWorkerData?.[customField.fieldLabel] ??
          getDefaultValueForCustomDataType(customField.fieldDataType),
      }),
      {},
    ),
  };
};

export type JobsiteWorkerUpdateInput = UpdateJobsiteWorkerMutationVariables;

export const getUpdateInput = (
  jobsiteWorker: JobsiteWorker,
  data: EditJobsiteWorkerFormData,
  dirtyFields: DeepMap<EditJobsiteWorkerFormData, true>,
  jobsiteCustomWorkerDataFields: JobsiteCustomWorkerDataField[] = [],
): JobsiteWorkerUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);
  const {
    hasEscortPrivileges,
    isExemptFromSwipeInactivity,
    isExemptFromSiteSpecificRefresher,
    startDate,
  } = data;

  return {
    jobsiteWorkerId: jobsiteWorker?.jobsiteWorkerId,
    jobsiteWorkerInput: {
      isAntipassbackExempt: hasEscortPrivileges.value === TRUE,
      isExemptFromSwipeInactivity: isExemptFromSwipeInactivity.value === TRUE,
      isExemptFromSiteSpecificRefresher:
        isExemptFromSiteSpecificRefresher.value === TRUE,
      startDate: startDate?.toDate() ?? null,
      stickerNumber: getUpdateInputValue('helmetNumber'),
      payrollId: getUpdateInputValue('payrollId'),
      trade: getUpdateInputValue('trade'),
      tradeExperience: getUpdateInputValue('tradeExperience'),
      jobTitle: getUpdateInputValue('jobTitle'),
      unionAffiliation: getUpdateInputValue('unionAffiliation'),
      classificationGroup: getUpdateInputValue('classificationGroup'),
      customWorkerData: jobsiteCustomWorkerDataFields.reduce(
        (acc, customField) => ({
          ...acc,
          // @ts-expect-error [function works, but typing dynamically-generated fields is annoying]
          [customField.fieldLabel]: getUpdateInputValue(customField.fieldLabel),
        }),
        {},
      ),
    },
  };
};
